import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, normalizeLanguage, type UiLanguage } from "../i18n";

/**
 * Segmented EN / 中文 toggle. Self-contained: tapping a language switches the
 * live UI immediately (and the detector caches the choice to localStorage).
 * Pass `persist` to also write the choice to the signed-in user's profile —
 * anonymous screens (SignIn) omit it and rely on the localStorage cache.
 */
export function LanguageSwitcher({
  persist,
  style,
}: {
  persist?: (lng: UiLanguage) => void;
  style?: React.CSSProperties;
}) {
  const { t, i18n } = useTranslation();
  const current = normalizeLanguage(i18n.language);

  const choose = (lng: UiLanguage) => {
    if (lng === current) return;
    void i18n.changeLanguage(lng);
    persist?.(lng);
  };

  return (
    <div
      role="group"
      aria-label={t("language.label")}
      style={{
        display: "inline-flex",
        border: "2px solid var(--tp-ink)",
        borderRadius: 999,
        overflow: "hidden",
        ...style,
      }}
    >
      {SUPPORTED_LANGUAGES.map((lng) => {
        const active = lng === current;
        return (
          <button
            key={lng}
            type="button"
            onClick={() => choose(lng)}
            aria-pressed={active}
            style={{
              border: "none",
              cursor: active ? "default" : "pointer",
              padding: "6px 14px",
              fontFamily: "var(--tp-font-body)",
              fontSize: 14,
              fontWeight: 600,
              background: active ? "var(--tp-ink)" : "transparent",
              color: active ? "var(--tp-surface)" : "var(--tp-ink)",
            }}
          >
            {t(`language.${lng}`)}
          </button>
        );
      })}
    </div>
  );
}
